import { FC, useMemo, useState } from 'react'
import {
  Text,
  IconRocket,
  BorderRadius6,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  GreyWhite,
  Primary12Regular,
  Primary13Regular,
  Primary13SemiBold,
  ShadowM,
  ShadowXs,
  Skeleton,
  Box,
  Flex,
  Link
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { automationsPaths } from 'Automations/router'
import {
  presentationToNodeTypes,
  useResolvedNodeDefinitions
} from 'Automations/nodeDefinitions'
import { NodeIconTemplate } from 'Automations/nodeDefinitions/components'
import { PublishedBadge } from 'Automations/components'
import { WorkflowIconStack } from '../WorkflowIcon'
import { WorkflowCardMenu } from './WorkflowCard.menu'
import { UNTITLED_AUTOMATION_NAME } from 'Automations/automationEditor/constants'
import { formatTimestamp, getLatestVersion } from 'Automations/helpers'

type Props = {
  automationId: string
  name: string
  description: string
  timestamp: string
}

export const WorkflowCard: FC<Props> = ({
  automationId,
  name,
  description,
  timestamp
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
  const definitions = useResolvedNodeDefinitions()

  const { data: versionsData, isLoading } = useQuery(
    dashboardv99990101Client.getAutomationVersions,
    { automationId }
  )

  const activeVersion = versionsData?.items.find(item => item.active)
  const latestVersion = getLatestVersion(versionsData?.items ?? [])

  const presentationToUse =
    activeVersion?.presentation ?? latestVersion?.presentation

  const usedNodeTypes = presentationToNodeTypes(
    presentationToUse,
    definitions,
    {
      filterNodeTypes: ['END', 'NOTE'],
      filterStartNodes: true
    }
  )

  const icons = useMemo(() => {
    if (usedNodeTypes.length === 0) {
      return [<PlaceholderIcon key="placeholder" />]
    }

    return usedNodeTypes.slice(0, 10).map((nodeType, index) => {
      const Icon = definitions[nodeType].icon

      return (
        <Box
          key={`${nodeType}-${index}`}
          outline={`${GreyWhite} 2px solid`}
          borderRadius={BorderRadius6}
          overflow="hidden"
        >
          <Icon boxSize="36px" />
        </Box>
      )
    })
  }, [usedNodeTypes, definitions])

  return (
    <Flex
      data-testid={`automations.workflowCard.${automationId}`}
      flexDir="column"
      backgroundColor={GreyWhite}
      border={`${GreyGrey30} 1px solid`}
      borderRadius={BorderRadius6}
      boxShadow={ShadowXs}
      padding="16px"
      transition="box-shadow ease-in-out 250ms"
      role="group"
      _hover={{ boxShadow: ShadowM }}
      position="relative"
    >
      <Flex
        flexDir="column"
        gap="16px"
        as={Link.Unstyled}
        to={automationsPaths.automationsEditor({ automationId })}
      >
        <Flex>
          {isLoading ? (
            <Skeleton height="36px" width="36px" />
          ) : (
            <WorkflowIconStack icons={icons} />
          )}
        </Flex>

        <Flex flexDir="column" gap="4px">
          {isLoading ? (
            <>
              <Skeleton height="16px" width="75%" />
              <Skeleton height="16px" width="50%" />
            </>
          ) : (
            <>
              <Text {...Primary13SemiBold} color={GreyGrey80}>
                {name || UNTITLED_AUTOMATION_NAME}
              </Text>
              <Text {...Primary13Regular} color={GreyGrey80}>
                {description}
              </Text>
            </>
          )}
        </Flex>

        <Flex justifyContent="space-between" alignItems="center">
          {isLoading ? (
            <Skeleton height="12px" width="108px" />
          ) : (
            <Text {...Primary12Regular} color={GreyGrey60}>
              {formatTimestamp(timestamp)}
            </Text>
          )}

          {isLoading ? (
            <Skeleton height="24px" width="49px" />
          ) : (
            <PublishedBadge
              status={activeVersion ? 'published' : 'unpublished'}
            />
          )}
        </Flex>
      </Flex>

      <Box
        opacity={isMenuOpen ? 1 : 0}
        _groupHover={{ opacity: 1 }}
        transition="opacity 250ms"
        position="absolute"
        top="16px"
        right="16px"
      >
        <WorkflowCardMenu
          automationId={automationId}
          onOpenChange={isOpen => setIsMenuOpen(isOpen)}
        />
      </Box>
    </Flex>
  )
}

const PlaceholderIcon: FC = () => (
  <Box
    outline={`${GreyWhite} 2px solid`}
    borderRadius={BorderRadius6}
    overflow="hidden"
  >
    <NodeIconTemplate icon={<IconRocket />} theme="grey" boxSize="36px" />
  </Box>
)
